import { useEffect } from 'react';
import { useAuth } from '../../Helpers/Contexts/AuthContext';
import { LEAF_RESOURCES, resourceForTab } from '../../Helpers/accessControl';
import { supabase } from '../../supabaseClient';

const VIEW_LOG_INTERVAL_MS = 10 * 60 * 1000;
export const DEMO_LOCK_TIP = 'Indisponível em demonstração';

const tabLabel = (tab) => LEAF_RESOURCES.find((r) => r.id === resourceForTab(tab))?.label || 'Painel';

/** Registra no audit_log cada tela que o usuário demo abre (no máximo 1x a cada 10 min por tela). */
export function useDemoTracking(activeTab, enabled) {
  const { isDemo } = useAuth();
  useEffect(() => {
    if (!isDemo || !enabled || !activeTab) return;
    const label = tabLabel(activeTab);
    const key = `demo_view_${label}`;
    try {
      const last = Number(sessionStorage.getItem(key) || 0);
      if (Date.now() - last < VIEW_LOG_INTERVAL_MS) return;
      sessionStorage.setItem(key, String(Date.now()));
    } catch { /* sem sessionStorage: registra mesmo assim */ }
    supabase.rpc('log_event', { p_action: 'DEMO_VIEW', p_summary: label, p_details: { tab: activeTab } })
      .then(() => {}, () => {});
  }, [isDemo, enabled, activeTab]);
}

// Botões de ação (gravam algo ou abrem formulário de gravação), pelo texto, aria-label ou dica
const WRITE_ACTION_RE = /\b(nov[oa]s?|adicionar|criar|cadastrar|salvar|editar|excluir|remover|apagar|desativar|reativar|registrar|assumir|iniciar|concluir|finalizar|enviar|anexar|upload|carregar|agendar|confirmar|atender|resolver|reabrir|alterar|trocar|substituir|importar|ajustar|transferir|vincular|notificar|instalar|abrir chamado|lan[çc]ar|dar baixa|movimentar|comentar|responder|convidar|marcar|colocar|retirar|limpar)\b/i;
// Levam dados para fora do sistema ou abrem o chamado inteiro: bloqueados mesmo sendo "ver"
const ALWAYS_LOCK_RE = /(exportar|download|baixar|imprimir|\b(ver|visualizar) chamado)/i;
// Navegação e leitura continuam liberadas mesmo que o texto lembre uma ação
const NAV_RE = /^(cancelar|fechar|voltar|ver|visualizar|aplicar|atualizar|sair|selecionar|filtrar|buscar|ampliar)/i;
const CANDIDATES = 'button, [role="button"], input[type="file"], input[type="checkbox"], input[type="radio"]';

const labelOf = (el) => [el.getAttribute('aria-label'), el.getAttribute('title'), el.textContent]
  .filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();

/** O elemento é uma ação de gravação? (exportado para teste) */
export function isWriteAction(label) {
  if (!label) return false;
  if (ALWAYS_LOCK_RE.test(label)) return true;
  return !NAV_RE.test(label) && WRITE_ACTION_RE.test(label);
}

const shouldLock = (el) => {
  // Menu lateral, barra superior e o aceite dos termos nunca são bloqueados
  if (el.closest('.MuiDrawer-root, header, [data-demo-allow]')) return false;
  // Caixas de seleção e upload só dentro de formulários (dialog/slide-over): filtros de tela continuam
  if (el.tagName === 'INPUT') return el.type === 'file' || !!el.closest('.MuiDialog-root, .MuiModal-root');
  return isWriteAction(labelOf(el));
};

/**
 * Modo demo: botões de ação aparecem desativados, com a dica "Indisponível em demonstração".
 * shortcut: identifica a ação pelo texto do botão na tela; um botão novo com verbo fora da lista
 * fica clicável (o banco recusa a gravação de qualquer forma). Se aparecer, inclua o verbo em WRITE_ACTION_RE.
 */
export function useDemoLock() {
  const { isDemo } = useAuth();
  useEffect(() => {
    if (!isDemo) return undefined;
    const scan = () => {
      document.querySelectorAll(CANDIDATES).forEach((el) => {
        if (el.dataset.demoLocked !== undefined) return;
        if (!shouldLock(el)) return;
        el.dataset.demoLocked = '';
        el.setAttribute('aria-disabled', 'true');
        el.setAttribute('title', DEMO_LOCK_TIP);
      });
    };
    // Clique e teclado em elemento bloqueado (ou dentro dele) não chegam ao React
    const block = (e) => {
      if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
      const el = e.target.closest?.('[data-demo-locked]');
      if (!el) return;
      e.preventDefault();
      e.stopPropagation();
    };
    // Tabelas grandes mudam o DOM muitas vezes seguidas: no máximo uma varredura por quadro
    let frame = 0;
    const scheduleScan = () => { if (!frame) frame = requestAnimationFrame(() => { frame = 0; scan(); }); };
    scan();
    const observer = new MutationObserver(scheduleScan);
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    ['click', 'mousedown', 'keydown', 'change'].forEach((t) => document.addEventListener(t, block, true));
    document.body.classList.add('demo-mode');
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      ['click', 'mousedown', 'keydown', 'change'].forEach((t) => document.removeEventListener(t, block, true));
      document.body.classList.remove('demo-mode');
      document.querySelectorAll('[data-demo-locked]').forEach((el) => {
        delete el.dataset.demoLocked;
        el.removeAttribute('aria-disabled');
        el.removeAttribute('title');
      });
    };
  }, [isDemo]);
}
